// services/everpro/everpro-order/track.ts - query.ts split (sub-module track/label)
// Label PDF + tracking status per AWB via S2 proxy.

import { everproFetch } from '../everpro-auth';
import type { EverproTrackingResult } from '../everpro-types';


export async function getEverproLabelUrl(awbNumber: string, brandId: string): Promise<string> {
  if (!brandId) throw new Error('Everpro: brandId wajib');

  // Label kadang 400 sepersekian detik setelah AWB terbit (propagasi Everpro).
  // Retry 400/404 3x jeda 2s; status lain (validasi asli) langsung dilempar.
  let lastMessage = '';
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 2000));
    let res: Response;
    try {
      res = await everproFetch(brandId, `/shipment/v1/orders/${encodeURIComponent(awbNumber)}/label`, {
        method: 'GET',
      });
    } catch {
      continue;
    }
    if (res.ok) {
      const json = await res.json();
      const url = json?.data?.url_label_pdf;
      if (url) return url;
      lastMessage = 'Label belum tersedia. Pastikan AWB sudah di-generate.';
      continue;
    }
    const detail = await res.text().catch(() => '');
    let message = '';
    try { message = JSON.parse(detail).message || ''; } catch { /* */ }
    lastMessage = message || `Everpro get label: ${res.status} ${res.statusText}`;
    if (res.status !== 400 && res.status !== 404) throw new Error(lastMessage);
  }
  throw new Error(lastMessage || 'Label belum tersedia. Pastikan AWB sudah di-generate.');
}



export async function trackEverproOrder(awbNumber: string, brandId: string): Promise<EverproTrackingResult> {
  if (!brandId) throw new Error('Everpro: brandId wajib');

  // Track by orderNo/AWB yang baru terbit bisa 404 sesaat (propagasi Everpro).
  // Retry 400/404 3x jeda 2s sebelum menyerah.
  let lastMessage = '';
  let res: Response | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 2000));
    try {
      res = await everproFetch(brandId, `/shipment/v1/orders/${encodeURIComponent(awbNumber)}/track`, {
        method: 'GET',
      });
    } catch {
      continue;
    }
    if (res.ok) break;
    const detail = await res.text().catch(() => '');
    try { lastMessage = JSON.parse(detail).message || ''; } catch { lastMessage = ''; }
    lastMessage = lastMessage || `Everpro tracking: ${res.status} ${res.statusText}`;
    if (res.status !== 400 && res.status !== 404) throw new Error(lastMessage);
    res = null;
  }
  if (!res || !res.ok) throw new Error(lastMessage || 'Everpro tracking gagal');

  const json = await res.json();
  // Envelope notFound dari proxy (HTTP 200 agar console bersih) → throw seperti
  // biasa supaya caller (modal/polling) tangani jalur error yang sama.
  if (json?.notFound) throw new Error('Nomor resi tidak ditemukan di Everpro');
  const d = json?.data ?? {};
  return {
    awbNumber: d.awb_number ?? awbNumber,
    trackingCode: d.tracking_code ?? '',
    statusId: d.status_id ?? 0,
    destination: d.destination,
    origin: d.origin,
    weight: d.weight,
    history: (d.tracking_history ?? []).map((h: any) => ({
      date: h.date,
      status: h.status,
      description: h.description,
      image_urls: h.image_urls,
    })),
    trackingUrl: d.tracking_url,
    deliveredAt: d.delivered_at,
  };
}
